import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, Target, Users, TrendingUp, Calendar, DollarSign,
  Award, FileText, AlertCircle, CheckCircle, BarChart3, Lightbulb, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import MetricCard from '../components/common/MetricCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ProgressBar from '../components/common/ProgressBar';

const ConsultantDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    activePortfolios: 0,
    totalProjects: 0,
    totalBudgetManaged: 0,
    avgSROI: 0
  });
  const [portfolios, setPortfolios] = useState([]);
  const [quarterlyInsights, setQuarterlyInsights] = useState([]);
  const [recommendedActions, setRecommendedActions] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Simulate consultant data - in production, this would fetch from backend
      setDashboardData({
        activePortfolios: 5,
        totalProjects: 23,
        totalBudgetManaged: 280000000,
        avgSROI: 4.2
      });

      // Mock portfolio data
      setPortfolios([
        {
          id: '1',
          companyName: 'TechVision Solutions',
          industry: 'IT Services',
          csrBudget: 75000000,
          projectsCount: 5,
          budgetUtilization: 68,
          avgSROI: 4.5,
          status: 'on_track',
          nextReview: '2024-03-15'
        },
        {
          id: '2',
          companyName: 'BuildCorp Infrastructure',
          industry: 'Construction',
          csrBudget: 120000000,
          projectsCount: 8,
          budgetUtilization: 85,
          avgSROI: 3.8,
          status: 'on_track',
          nextReview: '2024-03-10'
        },
        {
          id: '3',
          companyName: 'HealthPlus Pharma',
          industry: 'Pharmaceuticals',
          csrBudget: 45000000,
          projectsCount: 4,
          budgetUtilization: 42,
          avgSROI: 5.2,
          status: 'attention_needed',
          nextReview: '2024-02-28'
        },
        {
          id: '4',
          companyName: 'GreenEnergy Corp',
          industry: 'Renewable Energy',
          csrBudget: 30000000,
          projectsCount: 3,
          budgetUtilization: 90,
          avgSROI: 4.1,
          status: 'on_track',
          nextReview: '2024-03-20'
        }
      ]);

      // Mock quarterly insights
      setQuarterlyInsights([
        {
          id: '1',
          client: 'TechVision Solutions',
          insight: 'Education projects showing strong SROI. Recommend scaling.',
          category: 'positive',
          date: '2024-01-20'
        },
        {
          id: '2',
          client: 'HealthPlus Pharma',
          insight: 'Low budget utilization. Need to expedite project approvals.',
          category: 'attention',
          date: '2024-01-18'
        },
        {
          id: '3',
          client: 'BuildCorp Infrastructure',
          insight: 'Rural development projects meeting all milestones on time.',
          category: 'positive',
          date: '2024-01-15'
        }
      ]);

      // Mock recommended actions
      setRecommendedActions([
        {
          id: '1',
          client: 'HealthPlus Pharma',
          action: 'Schedule CSR Committee meeting for Q4 project approvals',
          priority: 'high',
          deadline: '2024-02-05'
        },
        {
          id: '2',
          client: 'TechVision Solutions',
          action: 'Prepare annual CSR impact report for board presentation',
          priority: 'high',
          deadline: '2024-02-15'
        },
        {
          id: '3',
          client: 'GreenEnergy Corp',
          action: 'Review and update CSR policy for FY 2024-25',
          priority: 'medium',
          deadline: '2024-03-01'
        },
        {
          id: '4',
          client: 'BuildCorp Infrastructure',
          action: 'Conduct SROI assessment for livelihood projects',
          priority: 'medium',
          deadline: '2024-03-10'
        }
      ]);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track': return 'text-green-600 bg-green-100';
      case 'attention_needed': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'on_track': return 'On Track';
      case 'attention_needed': return 'Needs Attention';
      case 'critical': return 'Critical';
      default: return 'Unknown';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getInsightColor = (category) => {
    switch (category) {
      case 'positive': return 'border-l-4 border-green-500 bg-green-50';
      case 'attention': return 'border-l-4 border-yellow-500 bg-yellow-50';
      case 'critical': return 'border-l-4 border-red-500 bg-red-50';
      default: return 'border-l-4 border-gray-300 bg-gray-50';
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Consultant Dashboard</h1>
              <p className="text-gray-600 mt-1">Fractional CSR Head - Strategic Advisory</p>
            </div>
            <Button
              onClick={() => navigate('/consultancy/policy-builder')}
              className="text-white"
              style={{ background: '#14B8A6' }}
            >
              <Plus className="w-5 h-5 mr-2" />
              New CSR Policy
            </Button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Active Portfolios"
            value={dashboardData.activePortfolios}
            subtitle="Client companies"
            icon={Briefcase}
            color="#14B8A6"
          />
          <MetricCard
            title="Total Projects"
            value={dashboardData.totalProjects}
            subtitle="Across all clients"
            icon={Target}
            color="#3B82F6"
          />
          <MetricCard
            title="Budget Managed"
            value={`₹${(dashboardData.totalBudgetManaged / 10000000).toFixed(0)}Cr`}
            subtitle="Total CSR allocation"
            icon={DollarSign}
            color="#10B981"
          />
          <MetricCard
            title="Avg SROI"
            value={`${dashboardData.avgSROI}:1`}
            subtitle="Portfolio average"
            icon={TrendingUp}
            color="#8B5CF6"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Client Portfolios */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Portfolios */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Client Portfolios</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/portfolios')}
                >
                  View All
                </Button>
              </div>

              <div className="space-y-4">
                {portfolios.map(portfolio => (
                  <div
                    key={portfolio.id}
                    className="p-5 border border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/portfolios/${portfolio.id}`)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {portfolio.companyName}
                        </h3>
                        <p className="text-sm text-gray-600">{portfolio.industry}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(portfolio.status)}`}>
                        {getStatusText(portfolio.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">CSR Budget</p>
                        <p className="text-sm font-semibold text-gray-900">
                          ₹{(portfolio.csrBudget / 10000000).toFixed(1)}Cr
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Projects</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {portfolio.projectsCount}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Avg SROI</p>
                        <p className="text-sm font-semibold text-teal-600">
                          {portfolio.avgSROI}:1
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Next Review</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {new Date(portfolio.nextReview).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>

                    {/* Budget Utilization */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600">Budget Utilization</span>
                        <span className="text-xs font-semibold text-gray-900">{portfolio.budgetUtilization}%</span>
                      </div>
                      <ProgressBar
                        value={portfolio.budgetUtilization}
                        max={100}
                        color={portfolio.budgetUtilization >= 70 ? '#10B981' : '#F59E0B'}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/portfolios/${portfolio.id}/projects`);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-teal-600 border-teal-600"
                      >
                        <Target className="w-4 h-4 mr-1" />
                        Projects
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/impact/story-generator?client=${portfolio.id}`);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-600"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Impact Story
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/portfolios/${portfolio.id}/reports`);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-purple-600 border-purple-600"
                      >
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Analytics
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quarterly Insights */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <Lightbulb className="w-6 h-6 mr-2 text-yellow-500" />
                Quarterly Insights
              </h2>
              <div className="space-y-4">
                {quarterlyInsights.map(insight => (
                  <div key={insight.id} className={`p-4 rounded-lg ${getInsightColor(insight.category)}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {insight.client}
                      </h4>
                      <span className="text-xs text-gray-500">
                        {new Date(insight.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{insight.insight}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Actions & Quick Links */}
          <div className="lg:col-span-1 space-y-6">
            {/* Recommended Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-teal-600" />
                Action Items
              </h3>

              <div className="space-y-3">
                {recommendedActions.map(action => (
                  <div key={action.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(action.priority)}`}>
                        {action.priority}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(action.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {action.action}
                    </p>
                    <p className="text-xs text-gray-600">{action.client}</p>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate('/actions')}
                variant="outline"
                className="w-full mt-4 text-teal-600 border-teal-600"
              >
                View All Actions
              </Button>
            </div>

            {/* Advisory Tools */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Advisory Tools</h3>
              <div className="space-y-2">
                <Button
                  onClick={() => navigate('/consultancy/policy-builder')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  CSR Policy Builder
                </Button>
                <Button
                  onClick={() => navigate('/impact/sroi-calculator')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  SROI Calculator
                </Button>
                <Button
                  onClick={() => navigate('/impact/story-generator')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Impact Story Generator
                </Button>
                <Button
                  onClick={() => navigate('/compliance/calendar')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Compliance Calendar
                </Button>
                <Button
                  onClick={() => navigate('/knowledge')}
                  variant="outline"
                  className="w-full justify-start text-gray-700 border-gray-300"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Knowledge Hub
                </Button>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Award className="w-5 h-5 mr-2 text-teal-600" />
                This Quarter
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Policies Created</span>
                  <span className="text-lg font-bold text-teal-600">3</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Impact Assessments</span>
                  <span className="text-lg font-bold text-teal-600">7</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Advisory Sessions</span>
                  <span className="text-lg font-bold text-teal-600">24</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Projects Launched</span>
                  <span className="text-lg font-bold text-teal-600">11</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantDashboard;
